/**
 * @file SearchPurchaseOrders.js
 * @version 1.43.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-10-05
 * @modified 2026-11-03
 *
 * 1.43.1 (2026-11-03, Cam — SO-D10696): archiving POs that are everything
 * bought for a customer order asks first whether the order goes too
 * (ArchivePurchaseOrdersFlow 1.3.0); an order kept with nothing bought makes
 * the result a warning.
 *
 * 1.43.0 (2026-11-02, Cam): "Add Supplier Ref & Confirm" in the row menu and
 * right-click, after Send to Supplier — a window laid out as Add Supplied Qty
 * (PoSupplierRefModal): the supplier's reference and Confirmed in one step.
 * Greyed with the reason for an archived PO, a PO-H, or a Xero-made PO while
 * the Xero PO sync is on (utils/poSupplierRef).
 *
 * 1.42.0 (2026-10-29, Cam — "PO modal needs to show linked PO-H when no SO-D
 * linking the two and PO table should show it too … on PO-H edit user should
 * be given option to inform haulage supplier of change via resending"): a
 * stock or self-billed PO collected by a third party shows its PO-H beside
 * its number as an order's PO shows its sales order — a lorry icon, the PO-H
 * and its status on hover, opened on click — and the PO-H's row shows the PO
 * it collects the same way (`haulagePo` / `haulageFor` from the search).
 * After a PO-H's Edit, one never sent is offered to the haulier ("send it
 * now?"), as a sent one is offered its revised copy.
 *
 * 1.41.0 (2026-10-28, Cam — rep mobile review): on a rep's phone each PO is
 * a RepDocCard — ONE tap target that opens the PO (the old squeezed row put
 * the PO number and its sales order side by side, and a tap there could open
 * the sales order instead). Supplier, who it is for, delivery day and status
 * in words (utils/repDocCards). The filters take Sales Orders' phone layout
 * (po-filter-search / po-filter-datetype classes, RepMobile.css).
 *
 * 1.37.0 (2026-10-19, Cam — "allow the edit button to work in PO-H modal and
 * PO search table"): Edit on a PO-H row opens its edit window
 * (HaulagePoDetailsModal — delivery, collection, notes); a sent PO-H whose
 * delivery, collection or haulier notes changed is offered as a revised PO-H
 * (Send to Supplier with the changes). "Cost & Supplied Qty" beside it records
 * a delivered order's PO-H's supplied quantities. A PO-H delivering somewhere
 * of its own shows "→ <name>" under the customer.
 *
 * 1.40.0 (2026-10-26, Cam — "could we add 'Archive' to the context and 3 dots
 * menus in search POs please? And to bulk actions? And if it is Xero source …
 * it will archive in Xero too"): Archive (row menu, right-click, bulk) now
 * works for a PO made in Xero while the Xero PO sync is nightly / off — the
 * server deletes it in Xero as it archives it (services/xeroPoDeleteService).
 * The answer may archive some and not others: those refused are listed with
 * why, and Xero-made ones beyond the per-request cap stay ticked to archive
 * again. Restore is greyed for a Xero-made PO (`restoreBlock` — Xero cannot
 * undelete a PO).
 *
 * 1.36.0 (2026-10-15, Cam): "Active only" replaces the Archived toggle — on by
 * default; off shows active AND archived POs together (it used to show archived
 * only). The Status column filter has an "Active / archived" section; ticking
 * Archived there brings archived POs in whatever the toggle says.
 *
 * 1.35.0 (2026-10-15, Cam): "Refused — redeliver…" / "Edit redelivery…" on a
 * PO-D / PO-H row acts on its customer order, the hub (RedeliverOrderModal,
 * utils/redelivery) — the PO itself stays as it is.
 *
 * 1.34.0 (2026-10-14, Cam) — "Add one bill for these N POs" (the bulk bar and
 * the right-click menu) is offered only with two or more POs ticked: it is
 * the action for several POs, so with one ticked it is simply not there (the
 * PO's own "Add Bill from this PO" is in its row menu). Add Bill splits a
 * supplier's one line over the POs' lines where it covers several.
 *
 * 1.33.0 (2026-10-13, Jade) — "Add Bill from this PO" stays available once a
 * PO is billed (a second or later bill for it; the save asks "a partial or
 * second bill?" as before). "Add supplier credit" beside it, billed or not:
 * Add Supplier Credit for the PO's supplier with the PO under Relates to and
 * the bills for its goods (`goodsBills`) — one of them is the bill the credit
 * is raised against. Greyed with the reason on an archived / voided PO, one
 * not saved here, or one with no supplier.
 *
 * 1.32.0 (2026-10-12, Cam) — a self-billed PO (us buying from a customer —
 * utils/selfBilledPo) is badged "Self-billed" beside its Stock badge.
 *
 * 1.31.0 (2026-10-09, Cam) — Edit on a delivered (or invoiced) order's PO-D
 * opens the full editor again, for corrections (words, item codes, supplier,
 * terms); its cost and supplied quantities change through the new "Cost &
 * supplied qty" action beside it (disabled with the reason elsewhere). A PO
 * made in Xero and a PO-H keep the narrowed window (utils/afterDelivery).
 *
 * 1.30.0 (2026-10-08, Cam) — the Status header's filter menu has a third
 * section, Supplied Qty: Recorded / To record.
 *
 * 1.29.2 (2026-10-08, Cam) — Add / Update Supplied Qty is no longer greyed
 * because the order is invoiced: until its invoice has gone out to the
 * customer it may change (the window offers the invoice changes).
 *
 * 1.29.0 (2026-10-06, Cam) — Edit on a PO made in Xero, or one whose customer
 * order is delivered (`costEdit` from the server), opens the narrowed window
 * (PoCostSuppliedModal): cost price and supplied quantities only, the cost of
 * a Xero-made PO sent to Xero. A stock PO-D records its supplied quantity
 * there too. The expanded lines show Supplied beside the ordered quantity,
 * and a PO-H's haulage lines.
 *
 * 1.26.0 (2026-10-05) — the archive sequence (checklist → cancellations →
 * POST /archive) moved to ArchivePurchaseOrdersFlow, unchanged, so the
 * transport planning boards run the same one for a row's PO.
 * 1.27.0 (2026-10-05, Cam) — a Xero-made PO is read-only here, whatever the
 * Xero PO sync switch says: Edit and Archive are disabled with the reason
 * ("This PO was made in Xero. Its cost is set from its bill when you add
 * it."). Add Bill comes back here after the bill is saved.
 * 1.28.0 (2026-10-06, Cam) — Divert… and Split Order… on a PO-D / PO-H act
 * on its customer order, the hub (utils/orderHub); a diverted order's POs
 * carry the Diverted pill.
 *
 * 1.25.0 (2026-10-03) — "Add Bill" from selected POs opens one bill against
 * them all (each line keeps its PO; a PO-H week's haulage is split there). The
 * "Separate bill per PO" choice is gone: it saved nothing, yet told the user
 * the other bills were waiting as drafts in Search Bills.
 *
 * 1.37.1 (2026-10-20, Cam): the expanded line view shows each line's quantity
 * and supplied quantity in its own unit with the tonnes beneath
 * (QtyWithTonnes), and the table export carries the same as text.
 *
 * 1.38.0 (2026-10-21, Cam): the expanded line view's Item Code and Description
 * columns are one — Purchase description, the item code a badge left of the
 * PO line's words (ItemCodeDescription). Qty and Supplied keep Cam's 125px
 * (SearchPurchaseOrders.css, renumbered). The export keeps the item code as
 * its own column.
 *
 * 1.39.0 (2026-10-22, Cam): a PO-H whose order is all from our stock offers
 * "Collection / Delivery" before the delivery date — the haulier's
 * collection is recorded then (AddSuppliedQuantityModal).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XERO_MADE_PO_READ_ONLY } from '../utils/poOwnership';
import { poEditOpensNarrowed, poCostSuppliedAction, poEditOpensHaulageWindow } from '../utils/afterDelivery';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchPurchaseOrders.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import NonInventoryBadge from './NonInventoryBadge';
import LastXeroSync from './LastXeroSync';
import Input from './Input';
import DateInput from './DateInput';
import ContactModal from './ContactModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import ConfirmDialog from './ConfirmDialog';
import ReactDOM from 'react-dom';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import TickCheckbox from './TickCheckbox';
import usePersistedState from '../hooks/usePersistedState';
import AlertDialog from './AlertDialog';
import SendPOEmailModal from './SendPOEmailModal';
import DocumentOutputModal from './DocumentOutputModal';
import { formatVatWithRate } from '../utils/taxTypes';
import { formatPrice } from '../utils/currencyFormatter';
import QtyWithTonnes from './QtyWithTonnes';
import ItemCodeDescription from './ItemCodeDescription';
import { quantityWithTonnesText, qtyFactsOf } from '../utils/qtyWithTonnes';
import { useSlideOutButton } from './SlideOutButton';
import RowActionsMenu from './RowActionsMenu';
import ArchivePurchaseOrdersFlow, { archiveResultMessage } from './ArchivePurchaseOrdersFlow';
import RestorePODialog from './RestorePODialog';
import { useDragSelect } from '../hooks/useDragSelect';
import useResizableColumns from '../hooks/useResizableColumns';
import DateRangeFilter from './DateRangeFilter';
import ProductRangeFilter, { productRangeParam } from './ProductRangeFilter';
import './ResizableColumns.css';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import ExportTableButton from './ExportTableButton';
import TableContextMenu from './TableContextMenu';
import EntityLink from './EntityLink';
import RepMobileFilterReset from './RepMobileFilterReset';
import useRepMobile from '../hooks/useRepMobile';
import RepDocCard, { RepDocCards } from './RepDocCard';
import { repPurchaseOrderCard } from '../utils/repDocCards';
import { useReveal, revealFilters } from '../utils/revealInSearch';
import { PO_STATUS_OPTIONS, poStatusLabel, poStatusBadgeClass, isArchivedPoStatus } from '../utils/poStatus';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useEntityModal } from './EntityModalProvider';
import AddSuppliedQuantityModal from './AddSuppliedQuantityModal';
import PoCostSuppliedModal from './PoCostSuppliedModal';
import PoSupplierRefModal from './PoSupplierRefModal';
import { SUPPLIER_REF_ACTION, supplierRefBlock, supplierRefMenuTitle } from '../utils/poSupplierRef';
import HaulagePoDetailsModal from './HaulagePoDetailsModal';
import { suppliedQtyBlock, isThirdParty } from '../utils/suppliedQty';
import DivertOrderFlow from './DivertOrderFlow';
import DivertedBadge from './DivertedBadge';
import RedeliverOrderModal from './RedeliverOrderModal';
import { redeliverMenuAction } from '../utils/redeliveryActions';
import SplitOrderModal from './SplitOrderModal';
import { hubForPoRow, divertBlockForPoRow, splitBlockForPoRow } from '../utils/orderHub';
import { SELF_BILLED_LABEL, SELF_BILLED_BADGE_TITLE } from '../utils/selfBilledPo';
import { addCreditPoRowBlock, creditPrefillFromPo, startSupplierCredit } from '../utils/supplierCredits';

// The most rows an export will fetch for "all results" — the export route's own cap.
const EXPORT_ALL_LIMIT = 1000;

/**
 * Columns that can be filtered from their header.
 *
 * Module-level so the object identity is stable — defined inline it would be a
 * new object every render, and every callback the hook memoises against it
 * would be rebuilt with it.
 *
 * The keys must match the whitelist in purchaseOrderService.searchPurchaseOrders.
 * An unknown key narrows nothing rather than erroring, so a mismatch shows up as
 * a filter that quietly does nothing.
 */
const COLUMN_FILTERS = {
  contact: { label: 'Supplier' },
  customer: { label: 'Customer' },
  /*
   * Two questions under one header. A PO's status and whether it has reached
   * its supplier are independent — an AUTHORISED PO that was never emailed is
   * the thing people are usually hunting for — and the Status cell already
   * shows both, so its header is where both are narrowed.
   *
   * Supplied Qty (Cam, 2026-10-08): "Recorded" — every goods line's supplied
   * quantity is there (the order line's, for a line bought for an order);
   * "To record" — it is due and a line has none. A PO not due yet, or made in
   * Xero (no supplied quantity of its own), is neither
   * (utils/suppliedQtyFilter.js on the server).
   */
  status: {
    label: 'Status',
    sections: [
      { key: 'status', label: 'Purchase order status' },
      { key: 'sent_to_contact', label: 'Sent to supplier' },
      { key: 'supplied_qty', label: 'Supplied Qty' },
      // Active / archived (Cam, 15 Oct) — the same question as the Active only
      // toggle, for those who prefer the column. Ticking Archived brings
      // archived POs into the search even with Active only on.
      { key: 'archived', label: 'Active / archived', values: ['Active', 'Archived'] }
    ]
  }
};

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', status: 'all', supplier: 'all', productRange: [],
  dateType: 'delivery', dateFrom: '', dateTo: '', datePreset: 'all-time',
  page: 1, limit: '20', includeAdditives: true, activeOnly: true
};


/*
 * Our reference — the customer order(s) a PO was raised from — behind a "sell"
 * icon beside the PO number: seen the moment it is hovered (0.3s fade in and
 * out), opened on click, never printed. The tip is rendered outside the table
 * (a portal, placed by the icon's position) because the cell clips anything
 * that overhangs it. It stays while the pointer is on it, so the second order
 * of a PO serving two can be clicked.
 */
function SoRefIcon({ sos, onOpen }) {
  return (
    <RefIcon
      icon="sell"
      ariaLabel={`Customer order ${sos.map(o => o.number).join(', ')}`}
      refs={sos.map(o => ({ key: o.id, label: o.number, open: () => onOpen(o.id) }))}
    />
  );
}

/*
 * A stock or self-billed PO and the PO-H raised for it (2026-10-29) — no
 * customer order between them, so no "sell" icon: a lorry on the PO's row
 * names its PO-H, a box on the PO-H's row the PO it collects. Same hover tip
 * and click as the order's; the tip gives the status (and "not sent yet").
 */
function HaulageLinkIcon({ po, onOpen }) {
  const h = po.haulagePo;
  const f = po.haulageFor;
  if (!h && !f) return null;
  const status = (s) => poStatusLabel(s);
  if (h) {
    return (
      <RefIcon
        icon="local_shipping"
        className="po-haulage-ref"
        ariaLabel={`Haulage order ${h.number}`}
        refs={[{
          key: h.id, label: h.number,
          sub: `${h.haulier ? `${h.haulier} · ` : ''}${status(h.status)}${h.sent ? '' : ' · not sent yet'}`,
          open: () => onOpen(h.xero_id)
        }]}
      />
    );
  }
  return (
    <RefIcon
      icon="inventory_2"
      className="po-haulage-ref"
      ariaLabel={`Collects ${f.number}`}
      refs={[{
        key: f.id, label: f.number,
        sub: `Collects for${f.self_billed ? ' (self-billed)' : ''} · ${f.supplier ? `${f.supplier} · ` : ''}${status(f.status)}`,
        open: () => onOpen(f.xero_id)
      }]}
    />
  );
}

/** The shared icon-with-tip: `refs` [{ key, label, sub?, open }]; the icon opens the first. */
function RefIcon({ icon, ariaLabel, refs, className = '' }) {
  const iconRef = useRef(null);
  const hideTimer = useRef(null);
  const [tip, setTip] = useState(null);      // { left, top } while mounted
  const [shown, setShown] = useState(false); // drives the fade

  const show = () => {
    clearTimeout(hideTimer.current);
    const r = iconRef.current?.getBoundingClientRect();
    if (!r) return;
    setTip({ left: r.right + 6, top: r.top + r.height / 2 });
    requestAnimationFrame(() => setShown(true));
  };
  const hide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setShown(false);
      hideTimer.current = setTimeout(() => setTip(null), 300);
    }, 80);
  };
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  return (
    <span className={`po-so-ref ${className}`.trim()} onClick={(e) => e.stopPropagation()}>
      <span
        ref={iconRef}
        className="material-symbols-rounded po-so-ref-icon"
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={() => refs[0]?.open()}
        onKeyDown={(e) => { if (e.key === 'Enter') refs[0]?.open(); }}
      >
        {icon}
      </span>
      {tip && ReactDOM.createPortal(
        <span
          className={`po-so-ref-tip${shown ? ' shown' : ''}`}
          role="tooltip"
          style={{ left: tip.left, top: tip.top }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          {refs.map(r => (
            <span key={r.key} className="po-so-ref-entry">
              <span className="po-so-ref-number" onClick={r.open}>{r.label}</span>
              {r.sub && <span className="po-so-ref-sub">{r.sub}</span>}
            </span>
          ))}
        </span>,
        document.body
      )}
    </span>
  );
}

function SearchPurchaseOrders({ user, onNavigate }) {
  const { openEntity } = useEntityModal();
  /*
   * Arriving from a modal's "Find in …", or from "View PO" after saving one
   * (utils/revealInSearch.js): the reference seeds the filters and the page
   * goes back to 1, both in the FIRST render's state, so the mount search is
   * the one that was asked for. Read before the usePersistedState calls below,
   * which it seeds.
   */
  const reveal = useReveal('search-purchase-orders');

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Drag-to-resize columns (10 cols: checkbox, expand, PO number, supplier,
  // customer, date, delivery, total, status, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchPurchaseOrders', 10);
  const [selectedContactId, setSelectedContactId] = usePersistedState('searchPOs.contactModalId', null);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = usePersistedState('searchPOs.poModalId', null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [sendEmailPO, setSendEmailPO] = useState(null); // PO to send email for
  // Add/Update Supplied Qty (or Update Delivery) on the PO's customer order.
  const [suppliedQtyOrderId, setSuppliedQtyOrderId] = useState(null);
  // The narrowed Edit window (cost / supplied only): { id, mode }.
  const [costEditFor, setCostEditFor] = useState(null);
  // "Add Supplier Ref & Confirm": the PO's local id while its window is open.
  const [supplierRefFor, setSupplierRefFor] = useState(null);
  // A PO-H's edit window (the search row), and the revised PO-H to offer after it.
  const [haulageEditFor, setHaulageEditFor] = useState(null);
  const [haulageRevised, setHaulageRevised] = useState(null); // { po, changes, haulier }
  // A PO-H never sent to its haulier, offered after its Edit (2026-10-29): { po, message }.
  const [haulageSendOffer, setHaulageSendOffer] = useState(null);
  // Divert / Split from a PO act on its customer order — the hub.
  const [divertOrderId, setDivertOrderId] = useState(null);
  // Refused — redeliver… on the PO's order (RedeliverOrderModal).
  const [redeliverOrderId, setRedeliverOrderId] = useState(null);
  const [splitOrderId, setSplitOrderId] = useState(null);
  // A PO the supplier already has: what changed since, and whether to say so.
  const [resendPrompt, setResendPrompt] = useState(null); // { po, changes, lastSentAt }

  /*
   * Send to Supplier. On a PO they already hold, look first at what has
   * changed since it went (its audit history, server-side) and, if anything
   * has, offer to put that in the email's subject and body — the same
   * "revised purchase order" an order edit sends.
   */
  const startSend = async (po) => {
    if (po.sentToContact) {
      try {
        const r = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(po.purchaseOrderID)}/changes-since-sent`,
          { credentials: 'include' });
        const d = await r.json();
        if (r.ok && d.changes?.length) {
          setResendPrompt({ po, changes: d.changes, lastSentAt: d.lastSentAt });
          return;
        }
      } catch (e) {
        console.warn('Could not read changes since the last send:', e.message);
      }
    }
    setSendEmailPO(po);
  };
  // Archive (what Delete was) and Restore — see services/poArchiveService.js.
  const [archiveFor, setArchiveFor] = useState(null);   // POs to archive, or null
  const [restoreFor, setRestoreFor] = useState(null);   // an archived PO, or null

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState(new Set());
  /**
   * Expand All is a mode, not a one-off action.
   *
   * It used to expand the rows on screen and nothing more, so paginating or
   * searching again returned everything to collapsed while the button still
   * read "Collapse All". Held here and re-applied whenever the results change,
   * so the button describes a state the table is actually in.
   *
   * Persisted, because it is a way of working rather than a per-visit choice —
   * someone who reads POs with their lines open wants that on Monday too.
   */
  const [expandAllMode, setExpandAllMode] = usePersistedState('searchPOs.expandAll', false);
  const [lineItems, setLineItems] = useState({});
  const [loadingLineItems, setLoadingLineItems] = useState({});
  const [expandingAll, setExpandingAll] = useState(false);
  // Server-computed distinct values per filterable column, for the whole result
  // set. Replaced on every search.
  const [facets, setFacets] = useState({});

  // Bulk selection state
  const [selectedPOs, setSelectedPOs] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);
  // Generate Bill(s) bulk dialog

  // Keyed by the Xero id string, matching the selection Set and the row keys.
  // A rep's phone: one card per PO instead of the table (1.41.0).
  const repMobile = useRepMobile();

  /**
   * Right-click a header to narrow by the values in the whole result set.
   *
   * Only the columns whose values repeat. A filter on PO number, a date or a
   * total would be a list where every option matches one row — a worse way to
   * find that row than reading it off the table.
   *
   * The keys must match the server's whitelist in purchaseOrderService; an
   * unknown key narrows nothing rather than erroring, so a mismatch here shows
   * up as a filter that quietly does nothing.
   */
  const cols = useColumnFilters(purchaseOrders, COLUMN_FILTERS, {
    // The values across the whole result set, computed server-side, so the menu
    // offers what is actually there rather than what landed on this page.
    facets,
    storageKey: 'searchPOs.columnFilters',
    // Back to page 1: staying on page 7 of a set that just shrank to two pages
    // shows an empty table for no visible reason.
    onChange: () => setPagination(prev => ({ ...prev, page: 1 }))
  });

  // How much of the table is still off to the right, for the cue.
  const [tableScrollRef, tableScroll] = useScrollAffordance([]);

  const ctxMenu = useTableContextMenu({
    rows: purchaseOrders,
    selectedIds: selectedPOs,
    getRowId: (po) => po.purchaseOrderID
  });


  const [pagination, setPagination] = usePersistedState('searchPOs.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }, { seed: reveal && { page: 1 } });

  // merge: a slot saved before a filter key existed (or by the old cross-search,
  // which wrote just { search } here) would otherwise come back missing keys
  // the screen believes it owns. seed: a "Find in …" handoff (see above).
  const [filters, setFilters] = usePersistedState('searchPOs.filters', {
    search: '',
    // Kept although the row no longer offers it: cross-searches from other
    // screens write this slot (openPOInSearch sends 'all_inc_deleted' so a
    // deleted PO it is pointing at still appears), and the Status header's
    // right-click filter narrows on top of it.
    status: 'all',
    supplier: 'all',
    productRange: [],
    dateType: 'delivery',
    dateFrom: '',
    dateTo: '',
    datePreset: 'all-time',
    page: 1,
    limit: '20',
    includeAdditives: true,
    // Active only (Cam, 15 Oct): on by default; off shows active and archived
    // POs together. The Status column filter's Active / archived section can
    // narrow it either way.
    activeOnly: true
  }, { merge: true, seed: revealFilters(reveal) });

  const [sortField, setSortField] = usePersistedState('searchPOs.sortField', 'date');
  const [sortDirection, setSortDirection] = usePersistedState('searchPOs.sortDirection', 'desc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });

  // Sync state
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');

  // "Sent Status Report" header button — emails a list of every PO not yet
  // sent to its supplier. Lives here rather than on the nav (where it used to
  // be a submenu entry that only ever opened a dialog).
  const [sendingReport, setSendingReport] = useState(false);
  const reportEmailRef = useRef(null);

  // Slideout buttons — re-measure when bulk actions bar appears or any modal
  // containing slideout buttons opens (sync custom-dates, generate bill), and
  // when the report button's label flips to "Sending...".
  useSlideOutButton([bulkActionsVisible, showSyncModal, sendingReport, selectedPOs.size]);

  const sendNotSentPOReport = async () => {
    const emailToSend = reportEmailRef.current?.value || user?.email;
    setSendingReport(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-orders/report/not-sent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToSend })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to send report');
      const totalFormatted = data.totalValue?.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
      setAlertDialog({
        isOpen: true,
        message: `Report sent successfully to ${emailToSend}. Found ${data.count} purchase order${data.count !== 1 ? 's' : ''} not sent (total value: £${totalFormatted}).`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error sending not-sent PO report:', error);
      setAlertDialog({ isOpen: true, message: `Failed to send report: ${error.message}`, type: 'error' });
    } finally {
      setSendingReport(false);
    }
  };

  const openNotSentReportDialog = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Email PO Sent Status Report',
      message: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            This will generate a report of all purchase orders that have not been sent to suppliers.
          </p>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Send report to:</label>
            <input type="email" ref={reportEmailRef} defaultValue={user?.email} />
          </div>
        </div>
      ),
      onConfirm: sendNotSentPOReport
    });
  };

  const isInitialMount = useRef(true);

  useEffect(() => {
    // Back to the page you were on, not page 1: the filters are restored from
    // the nav store, so the page number should be too. (Arriving with a
    // search handed over, both were seeded above — page 1.)
    searchPurchaseOrders(pagination.page || 1);
    // Mark initial mount as complete after a short delay to let state settle
    setTimeout(() => {
      isInitialMount.current = false;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search on filter change (skip during initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    const timeoutId = setTimeout(() => {
      searchPurchaseOrders(1);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search
  // the same way changing a filter above the table does.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchPurchaseOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  // The search's query string, for a page of results — shared by the screen and
  // by the export's "all results", so the two cannot filter differently.
  // Archived POs come into the search when Active only is off, or when the
  // Status column filter's Active / archived section ticks anything but Active.
  const archivedPicked = cols.selected?.archived;
  const showArchived = filters.activeOnly === false
    || (Array.isArray(archivedPicked) && archivedPicked.some(v => v !== 'Active'));

  const searchParamsFor = (page, limit, searchOverride) => {
        // Status filter mapping:
        //   'all'             → empty: backend applies default exclusion of DELETED/VOIDED
        //   'all_inc_deleted' → sentinel: backend skips the default exclusion
        //   anything else     → exact match
        const cleanFilters = {
          search: searchOverride !== undefined ? searchOverride : (filters.search || ''),
          // Active only (on by default) leaves archived POs (stored as DELETED)
          // and voided ones out; off, or Archived ticked in the Status column
          // filter, takes every status ('all_inc_deleted'). A status chosen in
          // the dropdown is sent as it is.
          status: (filters.status && filters.status !== 'all')
            ? filters.status
            : (showArchived ? 'all_inc_deleted' : ''),
          // Guarded with `||` rather than a bare `=== 'all'` check: a persisted
          // filters object saved before these two keys existed has them
          // undefined, which URLSearchParams would otherwise serialise as the
          // string "undefined" and the server would treat as a real value.
          supplierId: (filters.supplier && filters.supplier !== 'all') ? filters.supplier : '',
          productRange: productRangeParam(filters.productRange),
          dateType: filters.dateType || 'delivery',
          dateFrom: filters.dateFrom || '',
          dateTo: filters.dateTo || '',
          limit: limit || filters.limit || 20,
          includeAdditives: filters.includeAdditives ? 'true' : 'false'
        };

        const queryParams = new URLSearchParams({
          ...cleanFilters,
          page: page,
          sortField: sortField,
          sortDirection: sortDirection
        });
        // Column-header filters travel with the search, so they narrow the whole
        // result set and survive paginating and re-searching.
        if (cols.queryValue) queryParams.set('columnFilters', cols.queryValue);
        return queryParams;
  };

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchPurchaseOrders = async (page = 1, searchOverride) => {
    const seq = latest.begin();
    setLoading(true);
    try {
        const queryParams = searchParamsFor(page, null, searchOverride);

        const response = await fetch(`${API_URL}/api/purchase-orders/search?${queryParams}`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (!latest.isLatest(seq)) return;   // superseded by a later search

        if (data.success) {
          setPurchaseOrders(data.purchaseOrders || []);
          if (data.facets) setFacets(data.facets);
          setPagination({
              page: data.currentPage || page,
              limit: parseInt(filters.limit),
              // totalCount, not total: this router redacts cost fields by NAME
              // and 'total' is one, so it arrives null for anyone without
              // costs.view and the pagination bar would never render.
              total: data.totalCount ?? data.total ?? 0,
              totalPages: data.totalPages || 0
          });
        } else if (data.purchaseOrders) {
          setPurchaseOrders(data.purchaseOrders);
          setPagination({
              page: page,
              limit: parseInt(filters.limit),
              total: data.totalCount ?? data.total ?? data.purchaseOrders.length,
              totalPages: Math.ceil((data.totalCount ?? data.total ?? data.purchaseOrders.length) / parseInt(filters.limit))
          });
        }
    } catch (error) {
        if (!latest.isLatest(seq)) return;
        console.error('Search failed:', error);
        setAlertDialog({ isOpen: true, message: 'Failed to search purchase orders: ' + error.message, type: 'error' });
    }
    if (latest.isLatest(seq)) setLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSortField('date');
    setSortDirection('desc');
    // Clear Filters clears the column headers too — they are filters, and
    // leaving them on after a button labelled "clear all filters" is the kind
    // of thing that has people reporting the search as broken.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Re-search when sort changes
  useEffect(() => {
    if (isInitialMount.current) return;
    searchPurchaseOrders(pagination.page || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchPurchaseOrders(newPage);
    }
  };

  const showContactDetails = (contactId) => {
    setSelectedContactId(contactId);
  };

  const showPurchaseOrderDetails = (purchaseOrderId) => {
    setSelectedPurchaseOrderId(purchaseOrderId);
  };

  const getStatusBadgeClass = poStatusBadgeClass;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getFirstLineOfAddress = (address) => {
    if (!address) return '-';
    // If it's a string, split by newline and get first line
    if (typeof address === 'string') {
      // Check if it's a JSON string
      try {
        const parsed = JSON.parse(address);
        if (typeof parsed === 'object' && parsed !== null) {
          // It's a JSON object - extract meaningful first line
          return parsed.addressLine1 || parsed.city || parsed.company_name || '-';
        }
      } catch (e) {
        // Not JSON, treat as plain string
      }
      return address.split('\n')[0].trim() || '-';
    }
    // If it's an object (JSONB from database), extract first meaningful field
    if (typeof address === 'object') {
      return address.addressLine1 || address.city || address.company_name || '-';
    }
    return '-';
  };

  const getPostcodeFromAddress = (address) => {
    if (!address) return null;
    // If it's a string, try to parse as JSON
    if (typeof address === 'string') {
      try {
        const parsed = JSON.parse(address);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed.postalCode || parsed.postcode || null;
        }
      } catch (e) {
        // Not JSON - try to extract postcode from string (UK format)
        const postcodeMatch = address.match(/[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}/i);
        return postcodeMatch ? postcodeMatch[0] : null;
      }
    }
    // If it's an object
    if (typeof address === 'object') {
      return address.postalCode || address.postcode || null;
    }
    return null;
  };

  const formatReference = (reference) => {
    if (!reference) return null;
    // If reference is a string, return it
    if (typeof reference === 'string') return reference;
    // If reference is an object (shouldn't happen, but defensive), convert to string
    if (typeof reference === 'object') {
      console.warn('Reference is an object, expected string:', reference);
      // If it's an address object, return the first line
      if (reference.addressLine1) return reference.addressLine1;
      return JSON.stringify(reference);
    }
    return String(reference);
  };

  const handleCreatePO = () => {
    if (onNavigate) {
      onNavigate('create-purchase-order');
    }
  };

  // Sync purchase orders from Xero (admin only)
  const syncPurchaseOrders = async (syncType = 'custom') => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Initiating sync...');
    setShowSyncModal(false);

    try {
      let startDate, endDate;
      const today = new Date().toISOString().split('T')[0];

      if (syncType === '3months') {
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today;
      } else if (syncType === 'full') {
        startDate = '2020-01-01';
        endDate = today;
      } else {
        startDate = syncDateFrom || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = syncDateTo || today;
      }

      const eventSource = new EventSource(
        `${API_URL}/api/purchase-orders/sync?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          const progress = data.totalPages > 0
            ? Math.min(95, Math.round((data.currentPage / data.totalPages) * 100))
            : Math.min(95, Math.round((data.currentPage * 100) / Math.max(data.currentPage + 1, 10)));
          setSyncProgress(progress);
          setSyncMessage(data.message || `Fetching page ${data.currentPage}... (${data.recordsFetched} records)`);
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Sync complete!');
          setAlertDialog({ isOpen: true, message: data.result.message || `Synced ${data.result.count} purchase orders`, type: 'success' });
          searchPurchaseOrders(1);
          eventSource.close();

          setTimeout(() => {
            setLoading(false);
            setSyncProgress(0);
            setSyncMessage('');
          }, 1000);
        } else if (data.type === 'error') {
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.error}`, type: 'error' });
          eventSource.close();
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
      };

      eventSource.onerror = () => {
        setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
        eventSource.close();
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
      };
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  const openSyncModal = () => {
    setSyncDateFrom(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setSyncDateTo(new Date().toISOString().split('T')[0]);
    setShowSyncModal(true);
  };

  // Toggle row expand for PO
  const toggleRowExpand = async (poId) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(poId)) {
      newExpanded.delete(poId);
    } else {
      newExpanded.add(poId);

      // Fetch line items if not already loaded
      if (!lineItems[poId]) {
        await fetchLineItems(poId);
      }
    }

    setExpandedRows(newExpanded);
  };

  const fetchLineItems = async (poId) => {
    setLoadingLineItems(prev => ({ ...prev, [poId]: true }));
    try {
      const response = await fetch(
        `${API_URL}/api/purchase-orders/${poId}/lines`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setLineItems(prev => ({ ...prev, [poId]: data.lines || [] }));
    } catch (error) {
      console.error('Failed to fetch PO lines:', error);
      setLineItems(prev => ({ ...prev, [poId]: [] }));
    } finally {
      setLoadingLineItems(prev => ({ ...prev, [poId]: false }));
    }
  };

  /**
   * The export payload: exactly the rows on screen, formatted the same way.
   *
   * Built at click time, not on every render — formatting 200 rows into strings
   * continuously on the chance somebody presses Export would be a real cost for
   * a rare action.
   */
  const buildExport = async ({ scope = 'page' } = {}) => {
    // "All results" fetches past the page on screen, with the same filters and
    // sort. Capped at the export's own row limit, and says so when it bites.
    const onPage = cols.rows;
    const totalAvailable = Math.max(Number(pagination.total) || 0, onPage.length);
    let source = onPage;
    if (scope === 'all' && totalAvailable > onPage.length) {
      try {
        const res = await fetch(
          `${API_URL}/api/purchase-orders/search?${searchParamsFor(1, EXPORT_ALL_LIMIT)}`,
          { credentials: 'include' });
        const data = await res.json();
        if (Array.isArray(data.purchaseOrders)) source = data.purchaseOrders;
      } catch (error) {
        console.error('Could not fetch every result for the export:', error);
      }
    }

    // Every visible PO's lines, in one request. Fetched at export time rather
    // than read off the expanded-row cache: the export should carry the detail
    // whether or not the user happened to expand anything, and the cache only
    // holds what has been opened.
    let lines = { ...lineItems };
    const missing = source
      .map((po) => po.purchaseOrderID)
      .filter((id) => !lines[id]);
    if (missing.length) {
      try {
        const response = await fetch(`${API_URL}/api/purchase-orders/lines/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: missing })
        });
        const data = await response.json();
        lines = { ...lines, ...(data.lines || {}) };
        // Kept, so expanding a row after exporting does not re-fetch it.
        setLineItems((prev) => ({ ...prev, ...(data.lines || {}) }));
      } catch (error) {
        // An export without line items is worth more than no export at all.
        console.error('Could not load line items for the export:', error);
      }
    }

    // Explicit widths: the detail tables use table-layout: fixed so that every
    // one of them on a page resolves to the same columns. Without it each sizes
    // to its own longest cell and a column of prices becomes a staircase.
    const detailColumns = [
      { key: 'code', label: 'Item code', width: '16%' },
      { key: 'description', label: 'Purchase description', width: '48%' },
      { key: 'qty', label: 'Qty', width: '10%', numeric: true },
      { key: 'unit', label: 'Unit price', width: '13%', numeric: true },
      { key: 'lineTotal', label: 'Line total', width: '13%', numeric: true }
    ];

    return ({
    title: 'Purchase Orders',
    kicker: 'PURCHASE ORDERS',
    rowNoun: source.length === 1 ? 'purchase order' : 'purchase orders',
    filename: `purchase-orders-${new Date().toISOString().slice(0, 10)}`,
    landscape: true,
    columns: [
      { key: 'po', label: 'PO Number', width: '11%' },
      { key: 'supplier', label: 'Supplier', width: '20%' },
      { key: 'customer', label: 'Customer', width: '20%' },
      { key: 'created', label: 'Created', width: '9%' },
      { key: 'delivery', label: 'Delivery', width: '9%' },
      { key: 'total', label: 'Total', width: '10%', numeric: true },
      { key: 'status', label: 'Status', width: '11%' }
    ],
    rows: source.map((po) => ({
      po: po.purchaseOrderNumber || '',
      supplier: po.contact?.name || '',
      customer: po.customerName || po.customer?.name || (po.isStockPO ? 'Stock' : ''),
      created: formatDate(po.date),
      delivery: formatDate(po.deliveryDate),
      total: formatCurrency(po.total),
      status: po.status || '',
      // The expanded view, on paper.
      detail: {
        columns: detailColumns,
        emptyLabel: 'No line items on this purchase order',
        rows: (lines[po.purchaseOrderID] || []).map((line) => ({
          code: line.itemCode || '',
          description: line.description || '',
          // Its own unit and the tonnes it comes to (utils/qtyWithTonnes).
          qty: quantityWithTonnesText(line.quantity, qtyFactsOf(line)),
          unit: line.unitAmount !== undefined && line.unitAmount !== null
            ? formatPrice(line.unitAmount) : '',
          lineTotal: line.lineAmount !== undefined && line.lineAmount !== null
            ? formatCurrency(line.lineAmount) : ''
        }))
      }
    })),
    totals: [{
      po: `${source.length} orders`,
      total: formatCurrency(source.reduce((sum, po) => sum + (Number(po.total) || 0), 0))
    }],
    criteria: exportCriteria(),
    available: { page: onPage.length, all: totalAvailable },
    scopeNote: scope === 'all' && totalAvailable > EXPORT_ALL_LIMIT
      ? `First ${EXPORT_ALL_LIMIT.toLocaleString('en-GB')} of ${totalAvailable.toLocaleString('en-GB')} results`
      : ''
    });
  };

  /** What produced this list, printed above it so the page can be checked. */
  const exportCriteria = () => {
    const out = [];
    if (filters.search) out.push({ label: 'Search', value: filters.search });
    if (filters.status && filters.status !== 'all') out.push({ label: 'Status', value: filters.status });
    if (productRangeParam(filters.productRange)) {
      out.push({ label: 'Product range', value: productRangeParam(filters.productRange) });
    }
    if (filters.dateFrom || filters.dateTo) {
      out.push({
        label: filters.dateType === 'created' ? 'Created' : 'Delivery',
        value: `${filters.dateFrom || 'any'} to ${filters.dateTo || 'any'}`
      });
    }
    // Column filters are the reason two exports of "the same" search can differ,
    // so they are named rather than folded into the row count.
    for (const [key, set] of Object.entries(cols.selected)) {
      if (!set) continue;
      const label = cols.definitions[key]?.label || key;
      out.push({ label, value: [...set].join(', ') || 'none' });
    }
    return out;
  };

  // Expand/Collapse All functionality
  const handleExpandAll = async () => {
    if (expandAllMode) {
      setExpandAllMode(false);
      setExpandedRows(new Set());
      return;
    }

    setExpandAllMode(true);
    await expandEveryRow(purchaseOrders);
  };

  /**
   * Open every row in the given set, fetching what is missing in ONE request.
   *
   * The previous version fired one request per row in parallel — 200 of them on
   * a full page, each re-running the same three lookups server-side. Tolerable
   * as a one-off click; not something to repeat on every page change, which is
   * exactly what Expand All being a persistent mode requires.
   */
  const expandEveryRow = useCallback(async (rows) => {
    if (!rows.length) { setExpandedRows(new Set()); return; }
    const allIds = new Set(rows.map(po => po.purchaseOrderID));
    const idsToFetch = rows
      .filter(po => !lineItems[po.purchaseOrderID])
      .map(po => po.purchaseOrderID);

    if (idsToFetch.length > 0) {
      setExpandingAll(true);
      const loadingState = {};
      idsToFetch.forEach(id => { loadingState[id] = true; });
      setLoadingLineItems(prev => ({ ...prev, ...loadingState }));
      try {
        const response = await fetch(`${API_URL}/api/purchase-orders/lines/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: idsToFetch })
        });
        const data = await response.json();
        setLineItems(prev => ({ ...prev, ...(data.lines || {}) }));
      } catch (error) {
        console.error('Failed to fetch PO lines:', error);
      } finally {
        const done = {};
        idsToFetch.forEach(id => { done[id] = false; });
        setLoadingLineItems(prev => ({ ...prev, ...done }));
        setExpandingAll(false);
      }
    }
    setExpandedRows(allIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  /**
   * Re-apply the mode whenever the results change — a new page, a new search.
   *
   * Without this the button says "Collapse All" over a table that paginated
   * itself back to collapsed, which is the state it claims to be the opposite
   * of.
   */
  useEffect(() => {
    if (!expandAllMode) return;
    if (!purchaseOrders.length) return;
    expandEveryRow(purchaseOrders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrders, expandAllMode]);

  const handleEditPO = (poId) => {
    if (onNavigate) {
      sessionStorage.setItem('editPOId', poId);
      onNavigate('create-purchase-order');
    }
  };

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedPOs(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  const toggleSelectPO = (poId) => {
    setSelectedPOs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poId)) {
        newSet.delete(poId);
      } else {
        newSet.add(poId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPOs.size === purchaseOrders.length) {
      setSelectedPOs(new Set());
    } else {
      setSelectedPOs(new Set(purchaseOrders.map(po => po.purchaseOrderID)));
    }
  };

  // Clear selection when search results change
  useEffect(() => {
    setSelectedPOs(new Set());
  }, [purchaseOrders]);

  // Handle bulk actions bar animation
  useEffect(() => {
    if (selectedPOs.size > 0) {
      if (!bulkActionsVisible || bulkActionsClosing) {
        // Show the bar (or cancel closing animation)
        setBulkActionsVisible(true);
        setBulkActionsClosing(false);
      }
    } else if (selectedPOs.size === 0 && bulkActionsVisible && !bulkActionsClosing) {
      // Start closing animation. No clearTimeout cleanup —
      // setBulkActionsClosing(true) re-runs this effect, and cancelling the
      // timer would leave the bar stuck visible.
      setBulkActionsClosing(true);
      setTimeout(() => {
        setBulkActionsVisible(false);
        setBulkActionsClosing(false);
      }, 250);
    }
  }, [selectedPOs.size, bulkActionsVisible, bulkActionsClosing]);

  // Bulk status change
  const handleBulkStatusChange = (newStatus) => {
    const selectedCount = selectedPOs.size;
    setConfirmDialog({
      isOpen: true,
      title: 'Change Status',
      message: `Are you sure you want to change the status of ${selectedCount} purchase order${selectedCount !== 1 ? 's' : ''} to "${newStatus}"?`,
      onConfirm: () => confirmBulkStatusChange(newStatus)
    });
  };

  const confirmBulkStatusChange = async (newStatus) => {
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-orders/bulk/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          poIds: Array.from(selectedPOs),
          status: newStatus
        })
      });
      const data = await response.json();

      if (data.success) {
        setAlertDialog({
          isOpen: true,
          message: data.message || `Status updated for ${data.updated} purchase order(s)`,
          type: 'success'
        });
        setSelectedPOs(new Set());
        searchPurchaseOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        message: `Failed to update status: ${error.message}`,
        type: 'error'
      });
    }
    setBulkActionLoading(false);
  };

  // Bill the selected POs: one bill against them all, each line keeping its PO.
  // CreateBill (and the server) refuse another supplier's PO.
  const openGenerateBillDialog = () => {
    // Validate: every selected PO must have a local dbId (needed to resolve lines).
    const selectedList = purchaseOrders.filter(po => selectedPOs.has(po.purchaseOrderID));
    const withoutDbId = selectedList.filter(po => !po.dbId);
    if (withoutDbId.length > 0) {
      setAlertDialog({
        isOpen: true,
        message: `${withoutDbId.length} of the selected POs have not yet been saved locally. Sync POs first.`,
        type: 'warning'
      });
      return;
    }
    const poIds = selectedList.map(po => po.dbId).filter(Boolean);
    // One bill for SEVERAL POs (Cam, 2026-10-14): one PO is billed from its row.
    if (poIds.length < 2) return;
    sessionStorage.setItem('createBillFromPOIds', JSON.stringify({ ids: poIds, mode: 'combined' }));
    sessionStorage.setItem('createBill.returnTo', 'search-purchase-orders');
    setSelectedPOs(new Set());
    if (onNavigate) onNavigate('create-bill');
  };

  // Bulk delete
  const [documentOutput, setDocumentOutput] = useState(null); // { mode, ids }
  const openDocuments = (mode, ids) => {
    const list = Array.from(ids || []).filter(Boolean);
    if (list.length === 0) return;
    setDocumentOutput({ mode, ids: list });
  };
  const dbIdsFor = (xeroIds) => purchaseOrders
    .filter((po) => xeroIds.has(po.purchaseOrderID))
    .map((po) => po.dbId);

  /*
   * Why a PO cannot be archived, or null. The server applies the same rules
   * (poArchiveService.refusal) and sends each row's verdict as `archiveBlock`
   * — which follows the Xero PO sync switch: once it is off, a Xero-made PO
   * is held here and can be archived. The local rule is only the fallback for
   * a response without it.
   */
  const archiveBlockFor = (po) => {
    if (!po.dbId) return 'not saved locally';
    if (po.archiveBlock !== undefined) return po.archiveBlock;
    const id = String(po.purchaseOrderID || '');
    if (isArchivedPoStatus(po.status)) return 'already archived';
    if (String(po.status || '').toUpperCase() === 'BILLED' || po.hasBill) return 'billed — the supplier has invoiced it';
    // Made in Xero: the server decides (it depends on the Xero PO sync switch).
    if (!(id.startsWith('TEMP-') || id.startsWith('LOCAL-'))) return 'made in Xero — reload the list to check whether it can be archived';
    return null;
  };

  /*
   * Why a PO cannot be edited, or null. From the server (`editBlock`, the same
   * rule the save applies — utils/poOwnership.poChangeRefusal, and the 'Edit
   * posted documents' permission for a billed PO); else the old local rule.
   */
  const editBlockFor = (po) => {
    if (po.editBlock !== undefined) return po.editBlock;
    const isLocal = po.purchaseOrderID?.toString().startsWith('TEMP-') || po.purchaseOrderID?.toString().startsWith('LOCAL-');
    return isLocal ? null : XERO_MADE_PO_READ_ONLY;
  };

  // The shape ArchivePOsFlow takes.
  const asArchiveRow = (po) => ({
    id: po.dbId,
    purchase_order_number: po.purchaseOrderNumber,
    xero_purchase_order_id: po.purchaseOrderID,
    po_type: po.poType,
    status: po.status,
    supplier_name: po.contact?.name || '',
    sent_to_contact: !!po.sentToContact,
    blocked: archiveBlockFor(po)
  });

  const archiveSelected = () => {
    const list = purchaseOrders.filter(po => selectedPOs.has(po.purchaseOrderID));
    if (list.length) setArchiveFor(list);
  };

  // After ArchivePurchaseOrdersFlow has archived them (POST /archive).
  const archiveDone = (data) => {
    setArchiveFor(null);
    // Xero-made POs beyond the per-request cap stay ticked, to archive again.
    const deferred = new Set((data.deferred_ids || []).map(Number));
    setSelectedPOs(new Set(purchaseOrders.filter(po => deferred.has(Number(po.dbId))).map(po => po.purchaseOrderID)));
    searchPurchaseOrders(pagination.page);
    setAlertDialog({
      isOpen: true,
      type: (data.failed || []).length || deferred.size || (data.orders_kept || []).length ? 'warning' : 'success',
      message: archiveResultMessage(data)
    });
  };

  // Shared by the row's RowActionsMenu and the right-click context menu, so the
  // two surfaces can never drift apart.
  const buildRowActions = (po) => {
    // Made in Xero, or a delivered order's PO-H: Edit opens the narrowed
    // window. A delivered order's PO-D opens the full editor (corrections,
    // Cam 2026-10-09) and has "Cost & Supplied Qty" beside it.
    // A PO-H: its own edit window — delivery, collection, notes (Cam, 2026-10-19).
    const haulageWindow = poEditOpensHaulageWindow(po);
    const narrowed = poEditOpensNarrowed(po);
    const costSupplied = poCostSuppliedAction(po);
    const editBlock = haulageWindow
      ? (po.editBlock || (isArchivedPoStatus(po.status) ? 'Cannot edit — it is archived' : null))
      : narrowed ? null : editBlockFor(po);
    const canEdit = !editBlock;
    // Archived is stored as DELETED, in whichever case an older PO was written.
    const isDeleted = isArchivedPoStatus(po.status);
    const archiveBlock = archiveBlockFor(po);
    const canSend = !isDeleted;
    // Billed already is no bar: there can be a second bill (Jade, 2026-10-13).
    const canBill = !isDeleted;
    const creditBlock = addCreditPoRowBlock(po);

    return [
      {
        key: 'view',
        readSafe: true,   // view/filter only — safe for view-only accounts
        icon: 'visibility',
        label: 'View Details',
        title: 'View Details',
        onClick: () => showPurchaseOrderDetails(po.purchaseOrderID),
      },
      {
        key: 'edit',
        icon: 'edit',
        label: 'Edit',
        disabled: !canEdit,
        title: !canEdit ? editBlock
          : haulageWindow ? 'Edit — its delivery, collection and notes (its lines follow the order)'
            : narrowed ? `Edit cost${po.costEdit.supplied ? ' and supplied quantities' : ''} — ${po.costEdit.reason}` : 'Edit',
        onClick: () => {
          if (!canEdit) return;
          if (haulageWindow) setHaulageEditFor(po);
          else if (narrowed) setCostEditFor({ id: po.purchaseOrderID, mode: 'scope' });
          else handleEditPO(po.purchaseOrderID);
        },
      },
      {
        key: 'cost-supplied',
        icon: 'price_change',
        label: 'Cost & Supplied Qty',
        disabled: !costSupplied.enabled,
        title: costSupplied.title,
        onClick: () => { if (costSupplied.enabled) setCostEditFor({ id: po.purchaseOrderID, mode: 'scope' }); },
      },
      // Archive where Delete used to be: the PO leaves its customer order and
      // the planning boards, but stays — history and all — and can be restored.
      isDeleted ? {
        key: 'restore',
        icon: 'unarchive',
        label: 'Restore',
        // Made in Xero: deleted there when archived, and Xero cannot undelete it.
        disabled: !po.dbId || !!po.restoreBlock,
        title: !po.dbId ? 'Cannot restore — not saved locally'
          : po.restoreBlock ? `Cannot restore — ${po.restoreBlock}`
            : 'Restore this PO — you choose whether it goes back on its order lines',
        onClick: () => { if (po.dbId && !po.restoreBlock) setRestoreFor(po); },
      } : {
        key: 'archive',
        icon: 'archive',
        label: 'Archive',
        disabled: !!archiveBlock,
        title: archiveBlock ? `Cannot archive — ${archiveBlock}`
          : po.xeroMade ? 'Archive — deletes it in Xero too (Xero cannot undo that); offers the supplier a cancellation if they already have it'
            : 'Archive — offers the supplier a cancellation if they already have it',
        onClick: () => { if (!archiveBlock) setArchiveFor([po]); },
      },
      /*
       * What was actually supplied — recorded on the customer order this PO
       * serves (its lines and this PO's move together). A PO-H's order is
       * third-party: status and quantities together (Update Delivery). Greyed
       * with the reason until it is due (utils/suppliedQty).
       */
      (() => {
        const sos = po.linkedSOs || [];
        const so = sos[0];
        // A stock PO-D (no customer order) keeps its own supplied quantity,
        // recorded in the narrowed Edit window (2026-10-06).
        const stockPo = !so && po.poType !== 'haulage' && !po.xeroCostOnly && !isArchivedPoStatus(po.status);
        if (stockPo) {
          const block = suppliedQtyBlock({ deliveryDate: po.deliveryDate, deliveryStatus: 'Delivered', terms: null, hasSupplyPos: false });
          return {
            key: 'supplied-qty',
            icon: 'inventory',
            label: 'Supplied Qty',
            disabled: !!block,
            title: block ? `Not yet — ${block}` : 'Record what was supplied on this stock PO',
            onClick: () => { if (!block) setCostEditFor({ id: po.purchaseOrderID, mode: 'supplied' }); }
          };
        }
        const thirdParty = isThirdParty(so?.terms);
        const block = !so ? 'Not raised from a customer order — nothing to record supplied quantities against'
          : sos.length > 1 ? `Serves ${sos.length} customer orders — record it on each order`
          // Invoiced is not the end (2026-10-08): until its invoice has gone
          // out it may change; the window says when it has.
          : suppliedQtyBlock({
            deliveryDate: so.delivery_date, deliveryStatus: so.delivery_status, terms: so.terms, hasSupplyPos: true,
            // A PO-H whose order buys nothing in: the haulier collects from our
            // stock, recorded before the delivery date (2026-10-22).
            collects: po.poType === 'haulage' && thirdParty && so.po_status === 'No POs Linked'
          });
        const collects = po.poType === 'haulage' && thirdParty && so?.po_status === 'No POs Linked';
        const label = collects ? 'Collection / Delivery' : thirdParty ? 'Update Delivery' : (po.suppliedQtyLines > 0 ? 'Update Supplied Qty' : 'Add Supplied Qty');
        return {
          key: 'supplied-qty',
          icon: thirdParty ? 'local_shipping' : 'inventory',
          label,
          disabled: !!block,
          title: block ? `Not yet — ${block}` : `${label} on ${so.number}`,
          onClick: () => { if (!block) setSuppliedQtyOrderId(so.id); }
        };
      })(),
      /*
       * Divert and Split act on the customer order this PO was raised for —
       * the hub — and every PO of that order follows, exactly as from the
       * order. Greyed with the reason for a stock PO, a Xero-made or archived
       * one, or an invoiced order (utils/orderHub).
       */
      (() => {
        const block = divertBlockForPoRow(po);
        const { so } = hubForPoRow(po);
        return {
          key: 'divert',
          icon: 'alt_route',
          label: 'Divert…',
          disabled: !!block,
          title: block || `Divert ${so.number} — the whole order, with its POs, to another customer`,
          onClick: () => { if (!block) setDivertOrderId(so.id); }
        };
      })(),
      /*
       * Refused — redeliver…: the order this PO was raised for was refused on
       * delivery; it is held and goes again. The PO stays as it is.
       */
      (() => {
        const { so, block } = hubForPoRow(po);
        if (block || !so) {
          return { key: 'redeliver', icon: 'assignment_return', label: 'Refused — redeliver…', disabled: true,
            title: block || 'Not raised from a customer order', onClick: () => {} };
        }
        return redeliverMenuAction({
          ...so,
          order_number: so.number,
          excel_agri_delivery_terms: so.terms,
          supplier_delivery_terms: so.supplier_terms,
          has_supply_pos: true
        }, user, setRedeliverOrderId);
      })(),
      (() => {
        const block = splitBlockForPoRow(po);
        const { so } = hubForPoRow(po);
        return {
          key: 'split',
          icon: 'call_split',
          label: 'Split Order…',
          disabled: !!block,
          title: block || `Split ${so.number} — part of it to other customers`,
          onClick: () => { if (!block) setSplitOrderId(so.id); }
        };
      })(),
      {
        key: 'send',
        icon: 'forward_to_inbox',
        label: 'Send to Supplier',
        disabled: !canSend,
        title: canSend ? 'Send to Supplier' : 'Cannot send — PO is deleted',
        onClick: () => { if (canSend) startSend(po); },
      },
      // The supplier's reference and Confirmed in one step (Cam, 2026-11-02).
      (() => {
        const facts = { status: po.status, poType: po.poType, xeroCostOnly: !!po.xeroCostOnly, saved: !!po.dbId,
          supplierReference: po.supplierReference };
        const block = supplierRefBlock(facts);
        return {
          key: 'supplier-ref',
          icon: 'task_alt',
          label: SUPPLIER_REF_ACTION,
          disabled: !!block,
          title: supplierRefMenuTitle(facts),
          onClick: () => { if (!block) setSupplierRefFor(po.dbId); },
        };
      })(),
      // Export: preview, then print, download or email to yourself — one
      // modal for one PO or a selection (DocumentOutputModal).
      {
        key: 'export',
        icon: 'ios_share',
        label: 'Export',
        title: po.dbId ? 'Export — preview, print, download or email to yourself' : 'Cannot export — this PO has not been saved locally',
        disabled: !po.dbId,
        onClick: () => openDocuments('download', [po.dbId]),
      },
      {
        key: 'bill',
        icon: 'receipt_long',
        label: 'Add Bill from this PO',
        disabled: !canBill,
        title: isDeleted ? 'Cannot bill — PO is archived'
          : po.hasBill ? 'Add another bill to this PO — it is billed already (a partial or second bill)' : 'Add Bill from this PO',
        onClick: () => {
          if (canBill) {
            sessionStorage.setItem('createBillFromPOId', po.dbId || po.purchaseOrderID);
            sessionStorage.setItem('createBill.returnTo', 'search-purchase-orders');
            onNavigate && onNavigate('create-bill');
          }
        },
      },
      // A supplier's credit for this PO, billed or not (Jade, 2026-10-13):
      // the PO and the bills for its goods are named, so a credit against
      // the one bill clears its query and is allocated to it.
      {
        key: 'supplier-credit',
        icon: 'note_add',
        label: 'Add supplier credit',
        disabled: !!creditBlock,
        title: creditBlock
          || ((po.goodsBills || []).length === 1
            ? `A credit from ${po.contact?.name || 'the supplier'} relating to this PO, against ${po.goodsBills[0].supplier_reference || po.goodsBills[0].bill_number || 'its bill'}`
            : `A credit from ${po.contact?.name || 'the supplier'} relating to this PO`),
        onClick: () => {
          if (creditBlock) return;
          sessionStorage.setItem('createBill.returnTo', 'search-purchase-orders');
          startSupplierCredit(onNavigate, creditPrefillFromPo(po));
        },
      },
    ];
  };

  return (
    <div className="search-purchase-orders">
      {/* The rep phone shell hides .search-header entirely — see RepMobile.css
          — and this stands in for it. Desktop renders nothing. */}
      <RepMobileFilterReset onReset={handleClearFilters} />
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1'}}>
          <span className="info">Search purchase orders, view details, edit, archive, send to supplier, or create bill.</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>POs raised in Xero sync in automatically; POs raised here stay local-only.<LastXeroSync entity="purchase_orders" /></span>
          </span>
        </div>
        <div className='search-header-inner' style={{ gridColumn: '2'}}>
          <div className="buttons slideout">
            <div>
              <ExportTableButton
                build={buildExport}
                user={user}
                rowCount={cols.rows.length}
                onError={(message) => setAlertDialog({ isOpen: true, message, type: 'error' })}
              />
            </div>
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <>
                <div>
                  <button data-write-action onClick={() => syncPurchaseOrders('3months')} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">sync</span>
                    </div>
                    <span className="btn-label">Sync 3 Months</span>
                  </button>
                </div>
                <div>
                  <button onClick={openSyncModal} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">date_range</span>
                    </div>
                    <span className="btn-label">Custom Dates</span>
                  </button>
                </div>
                <div>
                  <button data-write-action onClick={() => syncPurchaseOrders('full')} className="btn-slideout admin-item" disabled={loading}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">cloud_download</span>
                    </div>
                    <span className="btn-label">Full Sync</span>
                  </button>
                </div>
              </>
            )}
            <div>
              <button onClick={openNotSentReportDialog} className="btn-slideout" disabled={loading || sendingReport}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">unsubscribe</span>
                </div>
                <span className="btn-label">{sendingReport ? 'Sending...' : 'Sent Status Report'}</span>
              </button>
            </div>
            <div>
              <button data-write-action onClick={handleCreatePO} className="btn-slideout" disabled={loading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group po-filter-search">
            <label>
              Search
              <SearchHelp
                amounts
                fields={['PO no.', 'reference', 'supplier', 'customer', 'account no.', 'delivery address', 'product', 'item code', 'additives', 'supplier ref.', 'total']}
              />
            </label>
            <div className="search-with-additives">
              <Input
                type="text"
                placeholder='Search POs...'
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onEnter={() => searchPurchaseOrders(1)}
                icon="search"
              />
              <TickCheckbox
                id="po-include-additives"
                checked={filters.includeAdditives}
                onChange={(e) => handleFilterChange('includeAdditives', e.target.checked)}
                label="+ADD"
                className="include-additives-pill"
                ariaLabel="Include additives"
              />
            </div>
          </div>

          {/* Status has gone from the row: right-clicking the Status header
              filters on it precisely and on the whole result set, which is
              strictly more than a single-value dropdown could do. */}
          <ProductRangeFilter
            side="purchase"
            value={filters.productRange}
            onChange={(value) => handleFilterChange('productRange', value)}
          />

          <DateRangeFilter
            from={filters.dateFrom}
            to={filters.dateTo}
            preset={filters.datePreset}
            onChange={({ from, to, preset }) => setFilters(prev => ({
              ...prev, dateFrom: from, dateTo: to, datePreset: preset, page: 1
            }))}
          />

          <div className="filter-group po-filter-datetype">
            <label>Date Type</label>
            <Dropdown
              value={filters.dateType}
              onChange={(value) => handleFilterChange('dateType', value)}
              options={[
                { value: 'delivery', label: 'Delivery' },
                { value: 'created', label: 'Created' }
              ]}
              placeholder="Select..."
            />
          </div>

          {/* Active only: on by default; off shows archived POs alongside the rest. */}
          <div className="filter-group" 
               title="On: active POs only. Off: archived POs are shown too. Or tick Archived in the Status column filter.">
            <label>Active only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="po-active-only"
                  type="checkbox"
                  checked={filters.activeOnly !== false}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                />
                <label htmlFor="po-active-only"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
              <label>Per Page</label>
              <Dropdown
                value={filters.limit}
                onChange={(value) => handleFilterChange('limit', value)}
                options={[
                  { value: '20', label: '20' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                  { value: '200', label: '200' }
                ]}
                placeholder="20"
              />
            </div>

          </div>
        </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {/* Bulk Actions Bar */}
      {bulkActionsVisible && (
        <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk Actions:</span>
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input
                type="checkbox"
                className="tick-check-input"
                id="bulk-select-indicator"
                checked={true}
                readOnly
              />
              <label htmlFor="bulk-select-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedPOs.size} item{selectedPOs.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <Dropdown
              value=""
              onChange={(value) => value && handleBulkStatusChange(value)}
              options={[
                { value: '', label: 'Change Status...' },
                ...PO_STATUS_OPTIONS.filter(o => ['DRAFT', 'AUTHORISED', 'SENT', 'CONFIRMED'].includes(o.value))
              ]}
              placeholder="Change Status..."
              disabled={bulkActionLoading}
            />
            {/* One bill for several POs — not offered for one (its row menu bills it). */}
            {selectedPOs.size >= 2 && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={openGenerateBillDialog}
                  disabled={bulkActionLoading}
                  title="One bill against all the ticked POs, each line keeping its PO"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">receipt_long</span>
                  </div>
                  <span className="btn-label">Add one bill for these {selectedPOs.size} POs</span>
                </button>
              </div>
            )}
            <div>
              <button data-write-action
                className="btn-slideout"
                onClick={archiveSelected}
                disabled={bulkActionLoading}
                title="Archive the selected POs (any already archived are listed and left as they are; any made in Xero are deleted in Xero too)"
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">archive</span>
                </div>
                <span className="btn-label">Archive Selected</span>
              </button>
            </div>
            <div>
              <button data-read-safe
                className="btn-slideout"
                onClick={() => setSelectedPOs(new Set())}
                disabled={bulkActionLoading}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="purchase-orders-panel">
        <div className='table-toolbar'>

          <button
            className="btn-secondary btn-sm"
            onClick={handleExpandAll}
            disabled={loading || expandingAll || purchaseOrders.length === 0}
          >
            <span className="material-symbols-rounded">
              {expandAllMode ? 'unfold_less' : 'unfold_more'}
            </span>
            {expandingAll ? 'Loading...' : (expandAllMode ? 'Collapse All' : 'Expand All')}
          </button>

          <ResetButton />

          {/* A column filter is invisible from anywhere but the header it was
              set on, and a table quietly missing rows is how people stop
              trusting a screen. This sits with the row counts and clears them
              all at once. */}
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />

          {pagination && pagination.total > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}

        </div>

        {/* Wrapped so the horizontal cue can sit still against the
            visible edge — inside the scrolling box it would travel
            with the columns and leave the screen. */}
        <div className="scroll-more-wrap">
        <div className="table-container scroll-more-box" ref={tableScrollRef}>
          {repMobile && (
            <RepDocCards loading={loading} count={cols.rows.length} empty="No purchase orders found">
              {cols.rows.map((po) => {
                const card = repPurchaseOrderCard(po);
                return (
                  <RepDocCard
                    key={po.purchaseOrderID}
                    number={card.number}
                    title={card.title}
                    place={card.place}
                    when={card.when}
                    flags={card.flags}
                    chip={card.chip}
                    total={card.total}
                    archived={card.archived}
                    onOpen={() => showPurchaseOrderDetails(po.purchaseOrderID)}
                    openLabel={`Open ${card.number} from ${card.title}`}
                  />
                );
              })}
            </RepDocCards>
          )}
          {!repMobile && (
          <table className="data-table purchase-orders-table resizable-table table-min-xl" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-pos"
                      checked={purchaseOrders.length > 0 && selectedPOs.size === purchaseOrders.length}
                      onChange={toggleSelectAll}
                      disabled={purchaseOrders.length === 0}
                    />
                    <label htmlFor="select-all-pos" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="expand-col"><ResizeHandle colIndex={1} /></th>
                <th className="sortable" onClick={() => handleSort('purchaseOrderNumber')}>
                  PO Number {sortField === 'purchaseOrderNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className={`sortable ${cols.headerProps('contact').className || ''}`.trim()}
                    onClick={() => handleSort('contact')}
                    onContextMenu={cols.headerProps('contact').onContextMenu}
                    title="Right-click to filter by the suppliers on this page">
                  Supplier {sortField === 'contact' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th className={`sortable ${cols.headerProps('customer').className || ''}`.trim()}
                    onClick={() => handleSort('customer')}
                    onContextMenu={cols.headerProps('customer').onContextMenu}
                    title="Right-click to filter by the customers on this page">
                  Customer {sortField === 'customer' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th className="sortable" onClick={() => handleSort('date')}>
                  Created {sortField === 'date' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className="sortable" onClick={() => handleSort('deliveryDate')}>
                  Delivery {sortField === 'deliveryDate' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="sortable" onClick={() => handleSort('total')}>
                  Total {sortField === 'total' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className={`status-col ${cols.headerProps('status').className || ''}`.trim()}
                    onContextMenu={cols.headerProps('status').onContextMenu}
                    title="Right-click to filter by the statuses on this page">
                  Status<ResizeHandle colIndex={8} />
                </th>
                <th className='actions-col'><ResizeHandle colIndex={9} /></th>
              </tr>
            </thead>
            <tbody className={loading ? 'loading-fade' : ''}>
              {/* Dots whenever a fetch is in flight, not only when the table
                  happens to be empty. Fading stale rows left the previous
                  search's results on screen looking like the new one's. */}
              {loading ? (
                <tr>
                  <td colSpan="10">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : cols.rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-results">No purchase orders found</td>
                </tr>
              ) : (
                cols.rows.map((po) => (
                  <React.Fragment key={po.purchaseOrderID}>
                    <tr
                      /* A PO row answers for itself: unsent gets the marker,
                         sent gets none. The partly-sent amber belongs to
                         Search Customer Orders, where a row stands for a whole
                         order and can genuinely be half done. */
                      className={`expandable-row ${selectedPOs.has(po.purchaseOrderID) ? 'selected' : ''} ${po.sentToContact ? '' : 'po-unsent'}`}
                      onClick={() => toggleRowExpand(po.purchaseOrderID)}
                      onContextMenu={ctxMenu.onRowContextMenu(po)}
                    >
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <label
                          className="tick-check-label"
                          style={{ justifyContent: 'center', gap: '0' }}
                          onMouseDown={dragSelect.onMouseDown(po.purchaseOrderID, selectedPOs.has(po.purchaseOrderID))}
                          onMouseEnter={dragSelect.onMouseEnter(po.purchaseOrderID)}
                          onClick={(e) => e.preventDefault()}
                        >
                          <input
                            type="checkbox"
                            className="tick-check-input"
                            id={`select-po-${po.purchaseOrderID}`}
                            checked={selectedPOs.has(po.purchaseOrderID)}
                            onChange={() => toggleSelectPO(po.purchaseOrderID)}
                          />
                          <label htmlFor={`select-po-${po.purchaseOrderID}`} className="tick-check">
                            <svg width="16px" height="16px" viewBox="0 0 18 18">
                              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                              <polyline points="1 9 7 14 15 4"></polyline>
                            </svg>
                          </label>
                        </label>
                      </td>
                      <td className="expand-cell">
                        <span className={`expand-icon ${expandedRows.has(po.purchaseOrderID) ? 'expanded' : ''}`}>
                          ▼
                        </span>
                      </td>
                      <td data-label="PO">
                        <div className="po-number-row">
                          <span
                            className="po-number-link"
                            onClick={(e) => { e.stopPropagation(); showPurchaseOrderDetails(po.purchaseOrderID); }}
                            style={{ cursor: 'pointer', color: 'var(--color-primary)', fontWeight: 'bold' }}
                          >
                            {po.purchaseOrderNumber}
                          </span>
                          {/* Our reference — the customer order(s) this PO was
                              raised from — kept to a hover, so it is never on a
                              PDF or email and never taken for the supplier's.
                              Click opens the order. */}
                          {po.linkedSOs?.length > 0 && (
                            <SoRefIcon sos={po.linkedSOs} onOpen={(id) => openEntity('customer_order', id)} />
                          )}
                          {/* A stock / self-billed PO ↔ its PO-H — no order between them. */}
                          <HaulageLinkIcon po={po} onOpen={(xeroId) => showPurchaseOrderDetails(xeroId)} />
                          {(() => {
                            const { so } = hubForPoRow(po);
                            return so ? (
                              <DivertedBadge
                                variant="icon"
                                diversions={so.diversions}
                                orderId={so.id}
                                orderNumber={so.number}
                                onChanged={() => searchPurchaseOrders(pagination.page)}
                              />
                            ) : null;
                          })()}
                          <NonInventoryBadge
                            present={po.nonInventoryLineCount > 0}
                            count={po.nonInventoryLineCount}
                            variant="icon"
                          />
                          {/* A supplied quantity has been recorded against this
                              PO's sales-order lines. It only exists on a local
                              PO raised from an SO, and it is what a bill should
                              be checked against rather than the ordered figure —
                              worth knowing before you start billing, not after.
                              No icon when there is none: absence is the norm. */}
                          {po.suppliedQtyLines > 0 && (
                            <span
                              className="supplied-qty-badge"
                              title={`Supplied quantity recorded on ${po.suppliedQtyLines} line${po.suppliedQtyLines === 1 ? '' : 's'} — check the bill against what was actually supplied, not what was ordered`}
                            >
                              <span className="material-symbols-rounded">inventory</span>
                            </span>
                          )}
                        </div>
                        {/* The supplier's reference, or nothing. A PO's own
                            reference is our customer order number (behind the
                            icon above); on an older Xero PO it is the mill's
                            number, which is theirs to quote, so it shows. */}
                        {(() => {
                          const ref = po.supplierReference
                            || (formatReference(po.reference) && !/^SO-/i.test(formatReference(po.reference)) ? formatReference(po.reference) : null);
                          return ref ? <div className="po-reference" title="Supplier's reference">{ref}</div> : null;
                        })()}
                      </td>
                      <td>
                        {/* Names only. A supplier's account number identifies
                            them to Xero, not to the person reading the row,
                            and the badge cost the name the width it needed. */}
                        <span className="name-with-badge">
                          {po.contactInDatabase && (po.contact?.accountId || po.contact?.contactID) ? (
                            <span
                              className="contact-link"
                              onClick={(e) => { e.stopPropagation(); showContactDetails(po.contact.accountId || po.contact.contactID); }}
                            >
                              {po.contact?.name || '-'}
                            </span>
                          ) : (
                            <span>{po.contact?.name || '-'}</span>
                          )}
                        </span>
                        {/* The supplier's OWN reference for this order —
                            what they quote back on the phone, and the thing
                            somebody is usually holding when they come looking
                            for a PO. Under the name because it belongs to
                            them, not to us. */}
                      </td>
                      <td data-label="Customer" onClick={(e) => e.stopPropagation()}>
                        {po.customer?.inDatabase && po.customer.name ? (
                          // Customer found in database - badge + clickable name
                          <span className="name-with-badge">
                            <span className={`account-badge-inline${!po.customer.accountNo ? ' empty' : ''}`}>
                              {po.customer.accountNo || '\u00A0'}
                            </span>
                            <span
                              className="contact-link"
                              onClick={() => showContactDetails(po.customer.accountId || po.customer.xeroContactId)}
                            >
                              {po.customer.name}
                            </span>
                          </span>
                        ) : po.isStockPO ? (
                          /* A stock PO has no customer — it is bought into our
                             own yard. The delivery address names us (H J Ward
                             Agriculture, The Old Grain Drier), which read as a
                             customer called "HJ Ward" and sent people looking
                             for an account that does not exist. The badge is
                             the whole answer, so nothing follows it. */
                          <span className="name-with-badge">
                            <span className="account-badge-inline" style={{ textTransform: 'uppercase' }}>
                              Stock
                            </span>
                            {/* Self-billed: us buying from a customer (utils/selfBilledPo). */}
                            {po.selfBilled && (
                              <span className="status-badge status-self-billed" title={SELF_BILLED_BADGE_TITLE}>{SELF_BILLED_LABEL}</span>
                            )}
                          </span>
                        ) : po.customerName ? (
                          // Customer name exists but not in database
                          <>
                            <span style={{ color: 'var(--text-primary)' }}>{po.customerName}</span>
                            {po.deliveryAddress && (
                              <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', marginTop: '2px' }}>
                                {(() => {
                                  const firstLine = getFirstLineOfAddress(po.deliveryAddress);
                                  const postcode = getPostcodeFromAddress(po.deliveryAddress);
                                  if (firstLine && firstLine.includes(',')) {
                                    const firstPart = firstLine.split(',')[0].trim();
                                    return postcode ? `${firstPart}, ${postcode}` : firstPart;
                                  }
                                  return postcode ? `${firstLine}, ${postcode}` : firstLine;
                                })()}
                              </div>
                            )}
                          </>
                        ) : (
                          // No customer info - show delivery address
                          <>
                            {po.deliveryAddress ? (
                              <div style={{ color: 'var(--text-primary)' }}>
                                {(() => {
                                  const firstLine = getFirstLineOfAddress(po.deliveryAddress);
                                  const postcode = getPostcodeFromAddress(po.deliveryAddress);
                                  if (firstLine && firstLine.includes(',')) {
                                    const firstPart = firstLine.split(',')[0].trim();
                                    return postcode ? `${firstPart}, ${postcode}` : firstPart;
                                  }
                                  return postcode ? `${firstLine}, ${postcode}` : firstLine;
                                })()}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </>
                        )}
                        {/* A PO-H delivering somewhere of its own (its edit window). */}
                        {po.deliveryOverride?.toOverridden && (
                          <div className="po-h-deliver-to-row" title="Delivery changed on this PO-H — the order is unchanged">
                            → {po.deliveryOverride.name}
                          </div>
                        )}
                      </td>
                      <td data-label="Created">{formatDate(po.date)}</td>
                      <td data-label="Delivery">{formatDate(po.deliveryDate)}</td>
                      <td data-label="Total" className="currency">{formatCurrency(po.total)}</td>
                      <td data-label="Status" className='status-col'>
                          <span className={getStatusBadgeClass(po.status)}>
                            {poStatusLabel(po.status)}
                          </span>
                          {/* Local-only indicator — PO has never been synced to Xero */}
                          {(po.purchaseOrderID?.toString().startsWith('TEMP-') || po.purchaseOrderID?.toString().startsWith('LOCAL-')) && (
                            <span
                              className="material-symbols-rounded"
                              style={{ fontSize: '16px', color: 'var(--color-error)', cursor: 'help' }}
                              title="Local only — not synced to Xero"
                            >
                              link_off
                            </span>
                          )}
                          {/* Sent to contact indicator */}
                          {po.sentToContact === true && (
                            <span
                              className="material-symbols-rounded"
                              style={{ fontSize: '16px', color: 'var(--color-success)', cursor: 'help' }}
                              title="Sent to supplier"
                            >
                              mail
                            </span>
                          )}
                          {po.sentToContact === false && (
                            <span
                              className="material-symbols-rounded"
                              style={{ fontSize: '16px', color: 'var(--color-error)', cursor: 'help' }}
                              title="Not sent to supplier"
                            >
                              unsubscribe
                            </span>
                          )}
                          {po.sentToContact === null && (
                            <span
                              className="material-symbols-rounded"
                              style={{ fontSize: '16px', color: 'var(--text-muted)', cursor: 'help' }}
                              title="Sent status unknown (not synced)"
                            >
                              mail
                            </span>
                          )}
                      </td>
                      <td className='actions-col' onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const isLocal = po.purchaseOrderID?.toString().startsWith('TEMP-') || po.purchaseOrderID?.toString().startsWith('LOCAL-');
                          const isXeroSynced = po.purchaseOrderID && !isLocal;

                          return (
                            <>
                              <RowActionsMenu actions={buildRowActions(po)} />
                              {isXeroSynced ? (
                                <span className="material-symbols-rounded info-icon">cloud_download</span>
                              ) : (
                                <span className="material-symbols-rounded info-icon local">cloud_upload</span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                    <tr className={`expanded-content ${expandedRows.has(po.purchaseOrderID) ? 'expanded' : ''}`}>
                      <td colSpan="10">
                        <div className="line-items-container">
                          {loadingLineItems[po.purchaseOrderID] ? (
                            <div className="loading-lines">Loading line items...</div>
                          ) : lineItems[po.purchaseOrderID]?.length > 0 ? (
                            <table className="line-items-table">
                              <thead>
                                <tr>
                                  <th>Purchase description</th>
                                  <th className="text-right">Quantity</th>
                                  <th className="text-right" title="What was supplied — the bill is checked against it">Supplied</th>
                                  <th className="text-right">Unit Amount</th>
                                  <th className="text-right">Line Amount</th>
                                  <th className="text-right">VAT</th>
                                  <th>Haulier</th>
                                  <th>Driver</th>
                                  <th>Vehicle</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lineItems[po.purchaseOrderID].map((line, idx) => (
                                  <tr key={idx}>
                                    {/* The item code as a badge left of the words (Cam, 21 Oct 2026). */}
                                    <td><ItemCodeDescription code={line.itemCode} itemId={line.itemId} description={line.description} /></td>
                                    {/* In the line's own unit with the tonnes beneath, ordered
                                        and supplied alike (utils/qtyWithTonnes — Cam, 20 Oct 2026). */}
                                    <td className="text-right"><QtyWithTonnes quantity={line.quantity} line={line} /></td>
                                    <td className="text-right" title={line.suppliedBlock || undefined}>
                                      {line.suppliedQuantity != null && line.kind !== 'haulage' ? <QtyWithTonnes quantity={line.suppliedQuantity} line={line} pallets={null} /> : '-'}
                                    </td>
                                    {/* A PO-H's lines carry no price: the haulier's charge arrives as its bill. */}
                                    <td className="text-right">{line.kind === 'po_h' ? '-' : formatPrice(line.unitAmount)}</td>
                                    <td className="text-right">{line.kind === 'po_h' ? '-' : formatCurrency(line.lineAmount)}</td>
                                    <td className="text-right">{line.kind === 'po_h' ? '-' : formatVatWithRate(line.taxAmount, line.taxType, formatCurrency)}</td>
                                    <td>{line.haulier || '-'}</td>
                                    <td>
                                      {line.driver
                                        ? <EntityLink type="driver" id={line.driverId}>{line.driver}</EntityLink>
                                        : '-'}
                                    </td>
                                    <td>{line.vehicle || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : expandedRows.has(po.purchaseOrderID) && !loadingLineItems[po.purchaseOrderID] ? (
                            <div className="no-lines">No line items found</div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          )}
        </div>
          <ScrollMore scrollRef={tableScrollRef} remaining={tableScroll.remainingX} direction="right" />
        </div>
      </div>

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => { setSelectedContactId(null); searchPurchaseOrders(pagination.page); }}
        />,
        document.body
      )}

      {selectedPurchaseOrderId && ReactDOM.createPortal(
        <PurchaseOrderModal
          user={user}
          purchaseOrderId={selectedPurchaseOrderId}
          onClose={() => { setSelectedPurchaseOrderId(null); searchPurchaseOrders(pagination.page); }}
          onEdit={handleEditPO}
        />,
        document.body
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {showSyncModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '340px', maxHeight: '600px' }}>
            <div className="modal-header">
              <h4 style={{ margin: '0' }}>Sync POs - Custom Dates</h4>
              <button className="btn-icon" onClick={() => setShowSyncModal(false)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ gap: 'var(--spacing-lg)', padding: 'var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)' }}>
              <div className="form-group">
                <label>From Date</label>
                <DateInput
                  value={syncDateFrom}
                  onChange={(e) => setSyncDateFrom(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>To Date</label>
                <DateInput
                  value={syncDateTo}
                  onChange={(e) => setSyncDateTo(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button data-read-safe className="btn-round btn-danger" onClick={() => setShowSyncModal(false)}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Cancel</span>
              </button>
              <button className="btn-slideout" onClick={() => syncPurchaseOrders('custom')}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">sync</span>
                </div>
                <span className="btn-label">Start Sync</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {archiveFor && (
        <ArchivePurchaseOrdersFlow
          pos={archiveFor.map(asArchiveRow)}
          user={user}
          onDone={archiveDone}
          onClose={() => setArchiveFor(null)}
        />
      )}

      {restoreFor && (
        <RestorePODialog
          po={restoreFor}
          onClose={() => setRestoreFor(null)}
          onRestored={({ status, relinked }) => {
            setRestoreFor(null);
            searchPurchaseOrders(pagination.page);
            setAlertDialog({
              isOpen: true, type: 'success',
              message: `${restoreFor.purchaseOrderNumber} restored as ${status}${relinked ? `, back on ${relinked} order line${relinked === 1 ? '' : 's'}` : ', on its own'}.`
            });
          }}
        />
      )}

      {costEditFor && (
        <PoCostSuppliedModal
          purchaseOrderId={costEditFor.id}
          mode={costEditFor.mode}
          onFullEdit={(id) => handleEditPO(id)}
          onClose={() => { setCostEditFor(null); searchPurchaseOrders(pagination.page); }}
          onSaved={(r) => setAlertDialog({
            isOpen: true, type: 'success',
            message: r?.xero?.ok && r.cost_lines_changed
              ? `${r.purchase_order_number} saved, and the new cost${r.cost_lines_changed === 1 ? ' is' : 's are'} in Xero.`
              : `${r?.purchase_order_number || 'The PO'} saved.`
          })}
        />
      )}
      {supplierRefFor && (
        <PoSupplierRefModal
          isOpen
          poDbId={supplierRefFor}
          onClose={() => { setSupplierRefFor(null); searchPurchaseOrders(pagination.page); }}
          onSaved={(r) => {
            setSupplierRefFor(null);
            searchPurchaseOrders(pagination.page);
            setAlertDialog({ isOpen: true, type: 'success', message: r?.message || 'Saved.' });
          }}
        />
      )}
      {haulageEditFor && (
        <HaulagePoDetailsModal
          poId={haulageEditFor.dbId || haulageEditFor.purchaseOrderID}
          onClose={() => { setHaulageEditFor(null); searchPurchaseOrders(pagination.page); }}
          onSaved={(out) => {
            const po = haulageEditFor;
            setHaulageEditFor(null);
            searchPurchaseOrders(pagination.page);
            if (out.revised) setHaulageRevised({ po, changes: out.revised.changes, haulier: out.revised.haulier });
            else if (out.send_offer) {
              // A stock / self-billed PO's PO-H the haulier has never had: offer it now.
              setHaulageSendOffer({
                po,
                message: `${out.changed ? `${out.purchase_order_number} saved. ` : ''}It has not been sent to ${out.send_offer.haulier || 'the haulier'} yet — send it now?`
              });
            } else setAlertDialog({ isOpen: true, type: 'success', message: out.changed ? `${out.purchase_order_number} saved.` : 'Nothing changed.' });
          }}
        />
      )}
      <ConfirmDialog
        isOpen={!!haulageRevised}
        title="Send the revised PO-H?"
        type="info"
        message={haulageRevised ? `${haulageRevised.po.purchaseOrderNumber} was sent to ${haulageRevised.haulier || 'the haulier'}: its ${haulageRevised.changes.map(c => c.replace(/ changed$/, '')).join(' and ')} changed. Send them the revised PO-H now?` : ''}
        confirmText="Send revised PO-H"
        confirmIcon="forward_to_inbox"
        onConfirm={() => {
          const r = haulageRevised;
          setHaulageRevised(null);
          setSendEmailPO({ ...r.po, revision: { summary: 'amended', changes: r.changes } });
        }}
        onCancel={() => setHaulageRevised(null)}
      />
      <ConfirmDialog
        isOpen={!!haulageSendOffer}
        title="Send the PO-H?"
        type="info"
        message={haulageSendOffer?.message || ''}
        confirmText="Send PO-H"
        confirmIcon="forward_to_inbox"
        cancelText="Not now"
        onConfirm={() => {
          const o = haulageSendOffer;
          setHaulageSendOffer(null);
          setSendEmailPO(o.po);
        }}
        onCancel={() => setHaulageSendOffer(null)}
      />
      {divertOrderId && (
        <DivertOrderFlow
          orderId={divertOrderId}
          user={user}
          onClose={() => { setDivertOrderId(null); searchPurchaseOrders(pagination.page); }}
        />
      )}
      {redeliverOrderId && (
        <RedeliverOrderModal
          orderId={redeliverOrderId}
          onClose={() => { setRedeliverOrderId(null); searchPurchaseOrders(pagination.page); }}
        />
      )}
      <SplitOrderModal
        isOpen={!!splitOrderId}
        orderId={splitOrderId}
        onClose={() => { setSplitOrderId(null); searchPurchaseOrders(pagination.page); }}
        onSuccess={() => { setSplitOrderId(null); searchPurchaseOrders(pagination.page); }}
      />

      <AddSuppliedQuantityModal
        user={user}
        isOpen={!!suppliedQtyOrderId}
        orderId={suppliedQtyOrderId}
        onClose={() => setSuppliedQtyOrderId(null)}
        onSuccess={() => { setSuppliedQtyOrderId(null); searchPurchaseOrders(pagination.page); }}
      />

      {resendPrompt && (
        <ConfirmDialog
          isOpen
          type="info"
          title={`Changes since ${resendPrompt.po.purchaseOrderNumber} was sent`}
          message={(
            <div className="resend-changes">
              <p>
                Since it was last sent to {resendPrompt.po.contact?.name || 'the supplier'}
                {resendPrompt.lastSentAt ? ` on ${new Date(resendPrompt.lastSentAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}:
              </p>
              <ul>{resendPrompt.changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
              <p>Summarise these changes in the email's subject and body?</p>
            </div>
          )}
          confirmText="Summarise the changes"
          cancelText="Send without"
          onConfirm={() => {
            const { po, changes } = resendPrompt;
            setResendPrompt(null);
            setSendEmailPO({ ...po, revision: { summary: 'amended', changes } });
          }}
          onCancel={() => {
            const { po } = resendPrompt;
            setResendPrompt(null);
            setSendEmailPO(po);
          }}
        />
      )}

      {sendEmailPO && ReactDOM.createPortal(
        <SendPOEmailModal
          purchaseOrder={sendEmailPO}
          user={user}
          onClose={() => setSendEmailPO(null)}
          onSent={(message) => {
            setAlertDialog({ isOpen: true, message, type: 'success' });
            searchPurchaseOrders(pagination.page); // Refresh to update sent status
          }}
        />,
        document.body
      )}

      {/* Table context menu (right-click) */}
      {documentOutput && (
        <DocumentOutputModal
          type="purchase_order"
          ids={documentOutput.ids}
          initialMode={documentOutput.mode}
          user={user}
          onClose={() => setDocumentOutput(null)}
          onDone={(message) => {
            setDocumentOutput(null);
            if (message) setAlertDialog({ isOpen: true, message, type: 'success' });
            searchPurchaseOrders(pagination.page);
          }}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          // One bill for several POs — only with two or more ticked.
          ids.size > 1 && {
            key: 'bulk-bill',
            icon: 'receipt_long',
            label: `Add one bill for these ${ids.size} POs`,
            disabled: bulkActionLoading,
            onClick: openGenerateBillDialog
          },
          // The selection is keyed by Xero id; the documents endpoint takes
          // the local id, which every row carries as dbId.
          {
            key: 'bulk-export',
            icon: 'ios_share',
            label: `Export ${ids.size} POs`,
            onClick: () => openDocuments('download', dbIdsFor(ids))
          },
          {
            key: 'bulk-archive',
            icon: 'archive',
            label: `Archive ${ids.size} POs`,
            disabled: bulkActionLoading,
            onClick: archiveSelected
          }
        ]}
        actions={[
          {
            key: 'select-all',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'select_all',
            label: selectedPOs.size === purchaseOrders.length && purchaseOrders.length > 0 ? 'Clear selection' : 'Select all',
            disabled: purchaseOrders.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'expand-all',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: expandedRows.size === purchaseOrders.length && purchaseOrders.length > 0 ? 'unfold_less' : 'unfold_more',
            label: expandedRows.size === purchaseOrders.length && purchaseOrders.length > 0 ? 'Collapse all' : 'Expand all',
            disabled: loading || expandingAll || purchaseOrders.length === 0,
            onClick: handleExpandAll
          },
          {
            key: 'refresh',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: () => searchPurchaseOrders(pagination.page)
          },
          {
            key: 'clear-filters',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />
      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

export default SearchPurchaseOrders;
